/**
 * Snapshot adapters transform flat ChipSnapshotData (collected at export time) into the
 * nested shapes that chip components expect — the same shapes normally provided by GQL
 * fragment reads. This lets chips render identically in PDF exports without a live GQL cache.
 *
 * Each adapter maps one SmartChipType. If no adapter is registered for a chip type,
 * resolveFromSnapshot returns null and the chip falls back to normal resolution.
 *
 * Most contributors don't need to modify this file unless they're changing what data a
 * chip component reads during rendering. See useSnapshotResolution for the consumer side.
 */
import {assert} from 'Asserts';
import {getPrefixToTypename} from 'graphql-client/APIIDPrefixToTypename';
import {maybeGetTypenameFromID} from 'graphql-client/getTypenameFromID';

import {ChipSnapshotData} from './SmartChipSnapshotContext';
import {SmartChipType} from './SmartChipTypes';

export type SnapshotAdapter = (data: ChipSnapshotData) => unknown;

/**
 * Typed output for the storable adapter covering chip-rendering fields.
 * This does NOT include popover/tooltip-only fields (e.g. occupiedCapacity, createdAt, barcode).
 * See BNCH-153158 for extending typed adapters to other chip types.
 */
export interface StorableAdapterOutput {
  __typename: string;
  id: string;
  name: string | null | undefined;
  editUrl: string | null | undefined;
  url: string | null | undefined;
  archiveRecord: {purpose: string} | null;
  storableSchema: {iconId: string | null | undefined};
  checkOutStatusInfo: {status: string} | null;
  restrictionStatus: string | null | undefined;
  sampleRoles: {isCurrentUserRestrictedSampleUser: boolean} | null;
  expirationInfo: {isTrackingEnabled: boolean; isExpired: boolean};
  validationState: null;
}

function baseFields(
  data: ChipSnapshotData
): Pick<StorableAdapterOutput, '__typename' | 'id' | 'name' | 'editUrl' | 'archiveRecord'> {
  const typename = data.id
    ? (maybeGetTypenameFromID(getPrefixToTypename(), data.id) ?? 'UnknownChipSummary')
    : 'UnknownChipSummary';
  return {
    __typename: typename,
    id: data.id,
    name: data.name,
    editUrl: data.url,
    archiveRecord: data.isArchived && data.archivePurpose != null ? {purpose: data.archivePurpose} : null,
  };
}

const storableAdapter = (data: ChipSnapshotData): StorableAdapterOutput => {
  assert(
    data.chipType === SmartChipType.STORABLE,
    `storableAdapter called with non-storable chip type: ${data.chipType}`
  );
  const storable = data;
  return {
    ...baseFields(data),
    url: data.url,
    storableSchema: {iconId: storable.storableSchemaIconId},
    checkOutStatusInfo: storable.checkOutStatus ? {status: storable.checkOutStatus} : null,
    restrictionStatus: storable.restrictionStatus,
    sampleRoles:
      storable.isCurrentUserAuthorized != null
        ? {isCurrentUserRestrictedSampleUser: storable.isCurrentUserAuthorized}
        : null,
    expirationInfo: {
      isTrackingEnabled: storable.isExpired != null,
      isExpired: storable.isExpired ?? false,
    },
    validationState: null,
  };
};

export const SNAPSHOT_ADAPTERS: Partial<Record<SmartChipType, SnapshotAdapter>> = {
  [SmartChipType.STORABLE]: storableAdapter,
};

export function resolveFromSnapshot(
  id: string,
  snapshotMap: ReadonlyMap<string, ChipSnapshotData>,
  getSmartChipTypeFromId: (id: string) => SmartChipType
): {item: unknown; isInaccessible: boolean} | null {
  const entry = snapshotMap.get(id);
  if (!entry) {
    return null;
  }

  const chipType = getSmartChipTypeFromId(id);
  const adapter = SNAPSHOT_ADAPTERS[chipType];
  if (!adapter) {
    return null;
  }

  return {
    item: entry.isInaccessible ? undefined : adapter(entry),
    isInaccessible: entry.isInaccessible,
  };
}
